import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const HeroIllustration = () => {
  return (
    <div className="relative w-full max-w-5xl mx-auto mt-12 md:mt-24 md:h-[600px] flex justify-center md:items-center">
      {/* Background SVG Path */}
      <svg className="absolute inset-0 w-full h-full hidden md:block" viewBox="0 0 1000 600" fill="none" xmlns="http://www.w3.org/2000/svg">
        <motion.path 
          d="M 120 180 C 120 180, 250 100, 350 250 C 450 400, 550 450, 650 350 C 750 250, 850 180, 850 180" 
          stroke="#BFE3E8" 
          strokeWidth="3" 
          strokeDasharray="6 6" 
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        <motion.path 
          d="M 350 250 C 350 250, 300 450, 450 550 C 600 650, 750 450, 650 350" 
          stroke="#BFE3E8" 
          strokeWidth="3" 
          strokeDasharray="6 6" 
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.5, ease: "easeInOut", delay: 0.5 }}
        />
        {/* Connection Dots */}
        <motion.circle cx="120" cy="180" r="6" fill="#1985A1" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 }} />
        <motion.circle cx="850" cy="180" r="6" fill="#1985A1" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.8 }} />
        <motion.circle cx="450" cy="550" r="6" fill="#E97862" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2.5 }} />
        <motion.circle cx="750" cy="450" r="6" fill="#E97862" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2.2 }} />
      </svg>

      {/* Main Profile Card (Center) */}
      <motion.div 
        className="relative z-20 w-full max-w-[320px] bg-white rounded-3xl shadow-xl shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-6 flex flex-col items-center mx-auto"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="w-24 h-24 rounded-full bg-meetoi-offwhite mb-4 border-4 border-white shadow-sm overflow-hidden flex items-center justify-center shrink-0">
          <SafeIcon icon={FiIcons.FiUser} className="text-4xl text-meetoi-blue" />
        </div>
        <h3 className="font-sora font-semibold text-xl text-meetoi-navy mb-1 text-center w-full truncate">Sophia Carter</h3>
        <p className="font-manrope text-sm text-meetoi-text/70 mb-6 text-center w-full">Growth Marketing Manager<br/>at Elevate</p>
        
        <div className="flex gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-meetoi-offwhite flex items-center justify-center text-meetoi-blue shrink-0"><SafeIcon icon={FiIcons.FiPhone} /></div>
          <div className="w-10 h-10 rounded-full bg-meetoi-offwhite flex items-center justify-center text-meetoi-blue shrink-0"><SafeIcon icon={FiIcons.FiMail} /></div>
          <div className="w-10 h-10 rounded-full bg-meetoi-offwhite flex items-center justify-center text-meetoi-blue shrink-0"><SafeIcon icon={FiIcons.FiLinkedin} /></div>
        </div>

        <button className="w-full bg-meetoi-blue text-white rounded-xl py-3 font-manrope font-semibold hover:bg-meetoi-navy transition-colors shrink-0">
          Save Contact
        </button>
      </motion.div>

      {/* Card: New Connection (Top Left) */}
      <motion.div 
        className="hidden md:flex absolute top-[10%] left-[10%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 items-center gap-4"
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="w-10 h-10 rounded-full bg-meetoi-offwhite overflow-hidden flex items-center justify-center border border-meetoi-aqua/50">
          <SafeIcon icon={FiIcons.FiUserPlus} className="text-meetoi-blue" />
        </div>
        <div>
          <div className="flex justify-between items-center gap-4">
            <h4 className="font-manrope font-bold text-sm text-meetoi-navy">New connection</h4>
            <span className="text-[10px] text-meetoi-text/50 font-medium">9:41 AM</span>
          </div>
          <p className="font-manrope text-xs text-meetoi-text/70">Met at Web Summit</p>
        </div>
      </motion.div>

      {/* Card: Shared via QR (Top Right) */}
      <motion.div 
        className="hidden md:flex absolute top-[20%] right-[10%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 items-center gap-4"
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
      >
        <div className="w-10 h-10 rounded-xl bg-meetoi-offwhite flex items-center justify-center">
          <SafeIcon icon={FiIcons.FiMaximize} className="text-meetoi-blue text-lg" />
        </div>
        <div>
          <div className="flex justify-between items-center gap-4">
            <h4 className="font-manrope font-bold text-sm text-meetoi-navy">Shared via QR</h4>
            <span className="text-[10px] text-meetoi-text/50 font-medium">9:41 AM</span>
          </div>
        </div>
      </motion.div>

      {/* Card: Added a note (Middle Right) */}
      <motion.div 
        className="hidden md:flex absolute top-[50%] right-[5%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 flex-col gap-2 max-w-[220px]"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-meetoi-offwhite flex items-center justify-center">
            <SafeIcon icon={FiIcons.FiFileText} className="text-meetoi-navy text-sm" />
          </div>
          <div className="flex-1 flex justify-between items-center">
            <h4 className="font-manrope font-bold text-sm text-meetoi-navy">Added a note</h4>
            <span className="text-[10px] text-meetoi-text/50 font-medium">10:15 AM</span>
          </div>
        </div>
        <p className="font-manrope text-xs text-meetoi-text/70 italic pl-11">
          "Great chat about growth strategies and AI."
        </p>
      </motion.div>

      {/* Card: Follow-up reminder (Bottom Right) */}
      <motion.div 
        className="hidden md:flex absolute bottom-[15%] right-[25%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 items-center gap-4"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.5 }}
      >
        <div className="w-10 h-10 rounded-full bg-meetoi-coral/10 flex items-center justify-center">
          <SafeIcon icon={FiIcons.FiBell} className="text-meetoi-coral text-lg" />
        </div>
        <div>
          <h4 className="font-manrope font-bold text-sm text-meetoi-navy">Follow-up reminder</h4>
          <p className="font-manrope text-xs text-meetoi-text/70 flex items-center mt-1">
            Tomorrow, 10:00 AM <SafeIcon icon={FiIcons.FiChevronDown} className="ml-1" />
          </p>
        </div>
      </motion.div>

      {/* Card: Followed up (Bottom Left) */}
      <motion.div 
        className="hidden md:flex absolute bottom-[20%] left-[10%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 items-center gap-4"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.8 }}
      >
        <div className="w-10 h-10 rounded-full bg-meetoi-blue/10 flex items-center justify-center">
          <SafeIcon icon={FiIcons.FiMail} className="text-meetoi-blue text-lg" />
        </div>
        <div>
          <div className="flex justify-between items-center gap-4">
            <h4 className="font-manrope font-bold text-sm text-meetoi-navy">Followed up</h4>
            <span className="text-[10px] text-meetoi-text/50 font-medium">Tomorrow</span>
          </div>
          <p className="font-manrope text-xs text-meetoi-text/70 mt-1 line-through opacity-70">Let's catch up next week!</p>
        </div>
      </motion.div>

      {/* Card: Stronger Relationship (Middle Left) */}
      <motion.div 
        className="hidden md:flex absolute top-[40%] left-[15%] z-10 bg-white rounded-2xl shadow-lg shadow-meetoi-navy/5 border border-meetoi-aqua/30 p-4 flex-col gap-3"
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 2.1 }}
      >
        <div className="flex justify-between items-start gap-6">
          <div>
            <h4 className="font-manrope font-bold text-sm text-meetoi-navy">Stronger<br/>relationship</h4>
            <p className="font-manrope text-xs text-meetoi-text/70 mt-1">Ongoing</p>
          </div>
          <SafeIcon icon={FiIcons.FiHeart} className="text-meetoi-coral text-xl" />
        </div>
        <div className="flex -space-x-2">
          <div className="w-6 h-6 rounded-full bg-meetoi-blue border-2 border-white"></div>
          <div className="w-6 h-6 rounded-full bg-meetoi-navy border-2 border-white"></div>
          <div className="w-6 h-6 rounded-full bg-meetoi-coral border-2 border-white"></div>
        </div>
      </motion.div>
    </div>
  );
};